import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ChevronRight,
  CheckCircle,
  Ruler,
  Zap,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { wellnessProducts } from '../data/wellness-products';
import AnimatedSection from '../components/AnimatedSection';

const WellnessProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    const foundProduct = wellnessProducts.find(p => p.id === productId);
    if (foundProduct) {
      setProduct(foundProduct);
      setActiveImageIndex(0);
    }
  }, [productId]);

  useEffect(() => {
    if (product && product.images && Array.isArray(product.images) && product.images.length > 0) {
      if (activeImageIndex >= product.images.length) {
        setActiveImageIndex(0);
      }
    }
  }, [product, activeImageIndex]);

  const scrollToDevis = (e) => {
    e.preventDefault();
    const element = document.getElementById('devis');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'sauna-interieur': 'Sauna intérieur',
      'sauna-exterieur': 'Sauna extérieur',
      'infrarouge': 'Cabine infrarouge'
    };
    return labels[category] || category;
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Produit introuvable</h1>
          <Button onClick={() => navigate('/espace-client')} className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800">
            Retour au catalogue
          </Button>
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : [product.thumbnail || product.image];

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <AnimatedSection animation="fade-in">
        <div className="bg-gray-50 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#c4d600] transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/espace-client" className="hover:text-[#c4d600] transition-colors">Espace Client</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-gray-800 font-medium">{product.name}</span>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Back Button */}
      <AnimatedSection animation="fade-in">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button
            variant="outline"
            onClick={() => navigate('/espace-client')}
            className="border-gray-300 hover:border-[#c4d600] hover:text-[#c4d600]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au catalogue
          </Button>
        </div>
      </AnimatedSection>

      {/* Hero Section with Image Gallery */}
      <AnimatedSection animation="fade-up">
        <div className="relative">
          <div className="relative h-[60vh] md:h-[70vh] overflow-hidden bg-gray-200">
            <img
              src={images[activeImageIndex]}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x800?text=Image+non+disponible';
                e.target.onerror = null;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            
            {/* Image Navigation */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeImageIndex === index ? 'bg-[#c4d600] w-8' : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Voir l'image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="max-w-7xl mx-auto">
                <span className="inline-block px-3 py-1 bg-[#c4d600] text-gray-800 text-sm font-semibold rounded mb-3">
                  {getCategoryLabel(product.category)}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold mb-2">{product.name}</h1>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <AnimatedSection animation="fade-up">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Description</h2>
                <div className="prose max-w-none text-gray-600 leading-relaxed">
                  <p className="mb-4">{product.fullDescription || product.shortDescription || product.description}</p>
                </div>
              </div>
            </AnimatedSection>

            {/* Standard Equipment */}
            {product.standardEquipment && product.standardEquipment.length > 0 && (
              <AnimatedSection animation="fade-up">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Équipement de série</h2>
                  <ul className="space-y-3">
                    {product.standardEquipment.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            )}

            {/* Dimensions */}
            {product.dimensions && product.dimensions.length > 0 && (
              <AnimatedSection animation="fade-up">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4">Dimensions disponibles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {product.dimensions.map((dim, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-2">
                          <Ruler className="w-5 h-5 text-[#c4d600] mr-2" />
                          <h3 className="font-semibold text-gray-800">{dim.model}</h3>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><span className="font-medium">Dimensions:</span> {dim.size}</p>
                          {dim.power && (
                            <p className="flex items-center">
                              <Zap className="w-4 h-4 text-[#c4d600] mr-1" />
                              <span className="font-medium">Puissance:</span> {dim.power}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <AnimatedSection animation="fade-up">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Intéressé par ce modèle ?</h3>
                <p className="text-gray-600 mb-6">
                  Demandez un devis personnalisé pour ce produit. Notre équipe vous recontactera sous 48h.
                </p>
                <Button
                  onClick={scrollToDevis}
                  className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold mb-4"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Demander un devis pour ce modèle
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Quote Form Section */}
      <AnimatedSection animation="fade-up">
        <div id="devis" className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Demandez votre devis pour {product.name}
              </h2>
              <p className="text-gray-600">
                Décrivez votre projet, nous vous recontactons sous 48h.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <p className="text-center text-gray-600 mb-6">
                Le formulaire de devis est disponible sur la page{' '}
                <Link to="/espace-client#devis" className="text-[#c4d600] hover:underline font-semibold">
                  Espace Client
                </Link>
                . Vous pouvez également nous contacter directement.
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  onClick={() => navigate('/espace-client#devis')}
                  className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
                >
                  Aller au formulaire
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-[#c4d600] text-[#c4d600] hover:bg-[#c4d600] hover:text-gray-800"
                >
                  <Link to="/contact">Nous contacter</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default WellnessProductDetailPage;
